import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredPages?: string[];
  requiredPermission?: {
    resource: string;
    action: string;
  };
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPages = [],
  requiredPermission,
}) => {
  const { isAuthenticated, isLoading, hasPermission, user, canAccessPage, hasResourcePermission } = useAuthStore();
  const location = useLocation();

  // Loading sırasında spinner göster
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // 1️⃣ Authentication check
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // 2️⃣ Admin & Super Admin = unlimited access
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    return <>{children}</>;
  }

  // 3️⃣ Role check (if required)
  if (requiredRole && !hasPermission(requiredRole)) {
    console.warn('⚠️ ProtectedRoute: Role access denied', { required: requiredRole, actual: user?.role });
    return <Navigate to="/unauthorized" replace />;
  }

  // 4️⃣ Page access check
  if (requiredPages.length > 0) {
    const hasAccess = requiredPages.every(page => canAccessPage(page));
    if (!hasAccess) {
      console.warn('⚠️ ProtectedRoute: Page access denied', { required: requiredPages });
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // 5️⃣ Resource permission check
  if (requiredPermission) {
    if (!hasResourcePermission(requiredPermission.resource, requiredPermission.action)) {
      console.warn('⚠️ ProtectedRoute: Permission denied', { resource: requiredPermission.resource, action: requiredPermission.action });
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};
